import { BoxGeometry, CylinderGeometry, Group, Mesh, SphereGeometry, type Object3D } from 'three';
import { Rng } from '../../core/rng';
import { flatMaterial } from '../../render/materials/cache';

const UNIT = new BoxGeometry(1, 1, 1);
const HEAD = new SphereGeometry(0.13, 10, 8);
const SKIN = ['#3b2417', '#4a2c1d', '#5a3825', '#6b4430', '#2e1c12'];
const TOPS = ['#e63946', '#f4a261', '#2a9d8f', '#ffd166', '#118ab2', '#ef476f', '#06d6a0', '#ffffff', '#6a4c93', '#264653'];
const BOTTOMS = ['#22223b', '#3d405b', '#1b263b', '#6c584c', '#283618'];
const PRINTS = ['#d62828', '#f77f00', '#fcbf49', '#2a9d8f', '#7b2cbf', '#0077b6'];

function part(color: string, w: number, h: number, d: number, x: number, y: number, z = 0): Mesh {
  const mesh = new Mesh(UNIT, flatMaterial(color));
  mesh.scale.set(w, h, d);
  mesh.position.set(x, y, z);
  mesh.castShadow = true;
  return mesh;
}

/** A limb that swings from its top: the mesh hangs below a pivot. */
function limb(color: string, w: number, length: number, x: number, y: number): Group {
  const pivot = new Group();
  pivot.position.set(x, y, 0);
  pivot.add(part(color, w, length, w, 0, -length / 2));
  return pivot;
}

export interface Person {
  root: Group;
  legs: [Object3D, Object3D];
  arms: [Object3D, Object3D];
}

/** Simple low-poly Accra pedestrian: some in kaba-style print skirts and head-wraps, some carrying loads. */
export function createPerson(seed: number): Person {
  const rng = new Rng(seed);
  const root = new Group();
  const skin = rng.pick(SKIN);
  const woman = rng.next() > 0.45;
  const bottom = woman ? rng.pick(PRINTS) : rng.pick(BOTTOMS);
  const legs: [Group, Group] = [limb(woman ? skin : bottom, 0.13, 0.8, -0.1, 0.82), limb(woman ? skin : bottom, 0.13, 0.8, 0.1, 0.82)];
  root.add(...legs, part(rng.pick(TOPS), 0.42, 0.56, 0.24, 0, 1.12));
  if (woman) root.add(part(bottom, 0.46, 0.5, 0.3, 0, 0.64));
  const arms: [Group, Group] = [limb(skin, 0.1, 0.58, -0.27, 1.36), limb(skin, 0.1, 0.58, 0.27, 1.36)];
  const head = new Mesh(HEAD, flatMaterial(skin));
  head.position.y = 1.55;
  root.add(...arms, head);
  if (woman && rng.next() > 0.4) root.add(part(rng.pick(PRINTS), 0.28, 0.12, 0.28, 0, 1.67));
  if (rng.next() > 0.65) {
    // Head load: a basin of goods, very common around Circle and Makola.
    const basin = new Mesh(new CylinderGeometry(0.36, 0.26, 0.16, 12), flatMaterial(rng.pick(['#c0c0c0', '#1d4e89', '#d62828'])));
    basin.position.y = 1.78;
    root.add(basin, part(rng.pick(['#e9c46a', '#f4a261', '#90be6d', '#e76f51']), 0.46, 0.14, 0.46, 0, 1.9));
  }
  return { root, legs, arms };
}

/** phase in radians; amount 0 (standing) to 1 (full stride). */
export function animateWalk(person: Person, phase: number, amount: number): void {
  const swing = Math.sin(phase) * 0.6 * amount;
  person.legs[0].rotation.x = swing;
  person.legs[1].rotation.x = -swing;
  person.arms[0].rotation.x = -swing * 0.8;
  person.arms[1].rotation.x = swing * 0.8;
  person.root.position.y = Math.abs(Math.sin(phase)) * 0.04 * amount;
}
